<script>
	import { fade, fly } from "svelte/transition";
	import { onMount } from "svelte";

	// --- Original Flow Logic ---
	let active = 1;

	const phases = [
		{
			id: 1,
			stage: "Discover",
			title: "Business Signal Capture",
			duration: "60-120 min",
			description:
				"Collect goals, constraints, workflows, data boundaries, and success metrics through guided prompts.",
			outcomes: ["Scope brief", "Risk profile", "Priority map"],
		},
		{
			id: 2,
			stage: "Discover",
			title: "Requirement Intelligence",
			duration: "1-2 hours",
			description:
				"Multi-agent analysis resolves ambiguity across integrations, compliance, and operational complexity.",
			outcomes: [
				"Integration matrix",
				"Control checklist",
				"Decision log",
			],
		},
		{
			id: 3,
			stage: "Design",
			title: "Platform Blueprint",
			duration: "2-4 hours",
			description:
				"OS Studio generates architecture, data contracts, role model, deployment topology, and delivery milestones.",
			outcomes: ["Architecture map", "Data model", "MVP plan"],
		},
		{
			id: 4,
			stage: "Build",
			title: "System Generation",
			duration: "1-2 days",
			description:
				"Generate typed backend, responsive frontend, APIs, security controls, and CI/CD scaffolding.",
			outcomes: [
				"Core services",
				"Operator dashboard",
				"Deployment pipeline",
			],
		},
		{
			id: 5,
			stage: "Build",
			title: "Quality and Security Validation",
			duration: "1 day",
			description:
				"Run testing layers for behavior, scale, and security posture before release approval.",
			outcomes: [
				"Test evidence",
				"Performance baseline",
				"Security gates",
			],
		},
		{
			id: 6,
			stage: "Launch",
			title: "MVP Walkthrough",
			duration: "Half day",
			description:
				"Validate the end-to-end user journey with stakeholders and close any final alignment gaps.",
			outcomes: [
				"Stakeholder sign-off",
				"Change list",
				"Go-live checklist",
			],
		},
		{
			id: 7,
			stage: "Launch",
			title: "Production Release",
			duration: "1-2 hours",
			description:
				"Deploy with observability, rollback protection, and operational runbooks active from day one.",
			outcomes: ["Live deployment", "Alerting baseline", "Ops runbook"],
		},
		{
			id: 8,
			stage: "Scale",
			title: "Continuous Optimization",
			duration: "Ongoing",
			description:
				"Improve throughput, reliability, and cost profile while adding phase-two capabilities.",
			outcomes: [
				"Weekly insights",
				"Optimization backlog",
				"Expansion roadmap",
			],
		},
	];

	const highlights = [
		{
			title: "Clear ownership model",
			detail: "Define who owns reliability, security, and product evolution before code reaches production.",
		},
		{
			title: "Security-first delivery",
			detail: "Policies, auth boundaries, and auditability are integrated into the build path, not bolted on later.",
		},
		{
			title: "Performance by default",
			detail: "Latency targets, caching strategies, and operational telemetry are set as part of architecture design.",
		},
	];

	$: selectedPhase = phases.find((phase) => phase.id === active) || phases[0];

	// --- New Multi-Agent Room Logic ---
	let activeRoom = "strategy";

	const rooms = {
		strategy: {
			id: "strategy",
			name: "Strategy & Ops",
			icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
			description:
				"Your dedicated product management team. They analyze your rough inputs, identify risks, and create a structured requirement specification.",
			agents: [
				{
					name: "Product Owner",
					action: "Analyzing Requirements",
					color: "text-blue-400",
				},
				{
					name: "Risk Analyst",
					action: "Compliance Check",
					color: "text-red-400",
				},
			],
			outputs: ["PRD Document", "Risk Matrix", "Compliance Checklist"],
		},
		architecture: {
			id: "architecture",
			name: "Architecture Lab",
			icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
			description:
				"The systems engineering unit. They turn the PRD into a technical blueprint, defining services, data models, and API contracts.",
			agents: [
				{
					name: "Sys Architect",
					action: "Designing Schema",
					color: "text-purple-400",
				},
				{
					name: "Integration Bot",
					action: "Mapping APIs",
					color: "text-indigo-400",
				},
			],
			outputs: ["System Blueprint", "DB Schema", "API Swagger"],
		},
		engineering: {
			id: "engineering",
			name: "Engineering Bay",
			icon: "M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4",
			description:
				"The core build team. They write clean, tested, production-grade code based on the architectural blueprints.",
			agents: [
				{
					name: "Backend Dev",
					action: "Compiling API",
					color: "text-emerald-400",
				},
				{
					name: "Frontend Dev",
					action: "Building UI",
					color: "text-pink-400",
				},
				{
					name: "DevOps Bot",
					action: "Configuring CI/CD",
					color: "text-orange-400",
				},
			],
			outputs: ["Source Code", "Docker Images", "Terraform Scripts"],
		},
		quality: {
			id: "quality",
			name: "Quality Assurance",
			icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
			description:
				"The gatekeepers. They rigorously test every component for bugs, security vulnerabilities, and performance bottlenecks.",
			agents: [
				{
					name: "QA Engineer",
					action: "Running E2E Tests",
					color: "text-yellow-400",
				},
				{
					name: "SecOps Bot",
					action: "Vulnerability Scan",
					color: "text-red-500",
				},
			],
			outputs: ["Test Reports", "Security Audit", "Performance Baseline"],
		},
	};
</script>

<section
	id="how-it-works"
	class="bg-[#0a0a0f] py-24 text-white relative min-h-screen overflow-hidden"
>
	<!-- Background Elements -->
	<div
		class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_90%)] pointer-events-none"
	></div>
	<div
		class="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 blur-[100px] rounded-full pointer-events-none"
	></div>
	<div
		class="absolute bottom-0 left-0 w-[500px] h-[500px] bg-neon-cyan/5 blur-[100px] rounded-full pointer-events-none"
	></div>

	<div class="container mx-auto max-w-7xl px-6 relative z-10">
		<!-- === SECTION 1: THE PROCESS (Timeline) === -->
		<div class="mb-20 text-center">
			<p
				class="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-primary"
			>
				How It Works
			</p>
			<h2 class="mb-6 text-4xl font-bold leading-tight md:text-6xl">
				From concept to live platform <br /><span class="text-gray-500"
					>in a controlled flow</span
				>
			</h2>
			<p class="mx-auto max-w-3xl text-lg leading-relaxed text-gray-400">
				OS Studio turns complex operating requirements into an
				executable system with a structured, auditable, and
				performance-aware delivery model.
			</p>
		</div>

		<!-- Stats Grid -->
		<div class="mb-16 grid gap-6 md:grid-cols-4">
			<div
				class="rounded-2xl border border-white/5 bg-[#13131f] p-6 hover:border-primary/30 transition-colors"
			>
				<p class="text-sm text-gray-400 mb-2">Time to MVP</p>
				<p class="text-3xl font-bold text-white">~4-5 days</p>
			</div>
			<div
				class="rounded-2xl border border-white/5 bg-[#13131f] p-6 hover:border-neon-cyan/30 transition-colors"
			>
				<p class="text-sm text-gray-400 mb-2">Delivery model</p>
				<p class="text-3xl font-bold text-white">8 phases</p>
			</div>
			<div
				class="rounded-2xl border border-white/5 bg-[#13131f] p-6 hover:border-purple-500/30 transition-colors"
			>
				<p class="text-sm text-gray-400 mb-2">Operational posture</p>
				<p class="text-3xl font-bold text-white">Secure by design</p>
			</div>
			<div
				class="rounded-2xl border border-white/5 bg-[#13131f] p-6 hover:border-blue-500/30 transition-colors"
			>
				<p class="text-sm text-gray-400 mb-2">After launch</p>
				<p class="text-3xl font-bold text-white">Continuous tuning</p>
			</div>
		</div>

		<!-- Timeline Split View -->
		<div class="grid gap-8 lg:grid-cols-[1fr_1fr] mb-32">
			<div
				class="rounded-3xl border border-white/5 bg-[#13131f] p-6 md:p-8"
			>
				<div class="mb-8 flex items-center justify-between gap-4">
					<h3 class="text-2xl font-bold">Execution Timeline</h3>
					<p class="text-xs uppercase tracking-widest text-gray-500">
						Select a phase
					</p>
				</div>
				<div class="grid gap-3 sm:grid-cols-2">
					{#each phases as phase}
						<button
							type="button"
							on:click={() => (active = phase.id)}
							class={`rounded-xl border p-4 text-left transition-all duration-300 ${
								active === phase.id
									? "border-primary bg-primary/10 shadow-[0_0_15px_rgba(124,58,237,0.15)] transform scale-[1.02]"
									: "border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/10"
							}`}
						>
							<div class="flex justify-between items-start mb-2">
								<p
									class={`text-xs font-bold uppercase tracking-wider ${active === phase.id ? "text-primary" : "text-gray-500"}`}
								>
									Phase {phase.id}
								</p>
								<span
									class={`text-[10px] px-2 py-0.5 rounded-full ${active === phase.id ? "bg-primary/20 text-primary-200" : "bg-white/5 text-gray-500"}`}
								>
									{phase.stage}
								</span>
							</div>
							<h4
								class={`text-sm font-bold ${active === phase.id ? "text-white" : "text-gray-300"}`}
							>
								{phase.title}
							</h4>
							<p class="mt-2 text-xs text-gray-500">
								{phase.duration}
							</p>
						</button>
					{/each}
				</div>
			</div>

			<div
				class="rounded-3xl border border-primary/20 bg-gradient-to-br from-[#1c1c2e] to-[#0a0a0f] p-8 md:p-10 relative overflow-hidden transition-all duration-500"
			>
				<!-- Details Content -->
				<div
					class="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-[80px] rounded-full pointer-events-none"
				></div>

				<div class="flex items-center gap-3 mb-6">
					<div
						class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold"
					>
						{selectedPhase.id}
					</div>
					<div>
						<p
							class="text-xs font-bold uppercase tracking-widest text-primary-300"
						>
							Phase Details
						</p>
						<h3 class="text-2xl font-bold text-white">
							{selectedPhase.title}
						</h3>
					</div>
				</div>

				<div class="flex gap-4 mb-8">
					<span
						class="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs text-gray-300"
						>{selectedPhase.stage}</span
					>
					<span
						class="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs text-gray-300"
						>{selectedPhase.duration}</span
					>
				</div>

				<p
					class="mb-8 text-lg leading-relaxed text-gray-300 border-l-2 border-primary/30 pl-4"
				>
					{selectedPhase.description}
				</p>

				<div>
					<p
						class="mb-4 text-xs font-bold uppercase tracking-widest text-gray-500"
					>
						Expected outcomes
					</p>
					<div class="space-y-3">
						{#each selectedPhase.outcomes as outcome}
							<div
								class="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/5"
							>
								<span class="text-neon-cyan">✓</span>
								<span class="text-sm text-gray-300"
									>{outcome}</span
								>
							</div>
						{/each}
					</div>
				</div>
			</div>
		</div>

		<!-- SEPARATOR -->
		<div class="my-32 border-t border-white/5 relative">
			<div
				class="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent"
			></div>
			<div
				class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0a0a0f] px-4 text-gray-500 text-sm tracking-widest uppercase"
			>
				Behind The Scenes
			</div>
		</div>

		<!-- === SECTION 2: THE TEAM (Multi-Agent Floor) === -->
		<div class="mb-16 text-center">
			<div
				class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-primary-300 text-xs font-mono uppercase tracking-widest mb-6"
			>
				<span
					class="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse"
				></span>
				Multi-Agent Collaboration
			</div>
			<h2 class="mb-6 text-4xl font-bold leading-tight md:text-6xl">
				Your 24/7 AI Engineering Team
			</h2>
			<p class="mx-auto max-w-3xl text-lg leading-relaxed text-gray-400">
				When you start a build, OS Studio spins up a dedicated virtual
				office. <br class="hidden md:block" />
				Specialized agents work in parallel to architect, build, and test
				your platform.
			</p>
		</div>

		<div class="grid lg:grid-cols-12 gap-8 lg:h-[600px]">
			<!-- Floor Plan Visualization -->
			<div class="lg:col-span-8 relative">
				<div
					class="absolute inset-0 bg-[#13131f]/50 rounded-3xl border border-white/5 backdrop-blur-sm -z-10"
				></div>

				<!-- Checkered Floor Pattern -->
				<div
					class="absolute inset-4 opacity-20 bg-[linear-gradient(45deg,#1c1c2e_25%,transparent_25%,transparent_75%,#1c1c2e_75%,#1c1c2e),linear-gradient(45deg,#1c1c2e_25%,transparent_25%,transparent_75%,#1c1c2e_75%,#1c1c2e)] bg-[size:20px_20px] bg-[position:0_0,10px_10px]"
				></div>

				<!-- Connectors (Animated Packets) -->
				<!-- Strategy -> Architecture -->
				<div
					class="absolute top-[25%] left-[48%] w-[4%] h-[2px] bg-white/10 z-0 hidden md:block"
				>
					<div
						class="absolute top-[-1px] left-0 w-1.5 h-1.5 bg-primary rounded-full animate-flow-h"
					></div>
				</div>
				<!-- Architecture -> Engineering -->
				<div
					class="absolute top-[48%] right-[25%] w-[2px] h-[4%] bg-white/10 z-0 hidden md:block"
				>
					<div
						class="absolute left-[-1px] top-0 w-1.5 h-1.5 bg-neon-cyan rounded-full animate-flow-v"
					></div>
				</div>
				<!-- Engineering -> Quality -->
				<div
					class="absolute bottom-[25%] left-[48%] w-[4%] h-[2px] bg-white/10 z-0 hidden md:block"
				>
					<div
						class="absolute top-[-1px] right-0 w-1.5 h-1.5 bg-purple-400 rounded-full animate-flow-h-rev"
					></div>
				</div>
				<!-- Quality -> Strategy (Feedback Loop) -->
				<div
					class="absolute top-[48%] left-[25%] w-[2px] h-[4%] bg-white/10 z-0 hidden md:block"
				>
					<div
						class="absolute left-[-1px] bottom-0 w-1.5 h-1.5 bg-orange-400 rounded-full animate-flow-v-rev"
					></div>
				</div>

				<div
					class="grid grid-cols-1 md:grid-cols-2 grid-rows-2 h-full gap-4 p-4 md:p-8"
				>
					<!-- Room: Strategy -->
					<button
						class="group relative rounded-2xl border transition-all duration-300 p-6 flex flex-col justify-between overflow-hidden text-left
						{activeRoom === 'strategy'
							? 'bg-[#1c1c2e] border-primary/50 shadow-[0_0_30px_rgba(124,58,237,0.15)] ring-1 ring-primary/30'
							: 'bg-[#1c1c2e]/40 border-white/5 hover:bg-[#1c1c2e]/60'}"
						on:click={() => (activeRoom = "strategy")}
					>
						<div
							class="flex justify-between items-start w-full relative z-10"
						>
							<div class="flex items-center gap-2">
								<span
									class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"
								></span>
								<h3
									class="font-bold text-white text-sm uppercase tracking-wide"
								>
									Strategy & Ops
								</h3>
							</div>
							<svg
								class="w-5 h-5 text-gray-500 group-hover:text-blue-400 transition-colors"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d={rooms.strategy.icon}
								/>
							</svg>
						</div>

						<!-- Agents -->
						<div class="space-y-3 relative z-10 mt-4">
							{#each rooms.strategy.agents as agent}
								<div class="flex items-center gap-3">
									<div
										class="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20"
									>
										<div
											class="w-4 h-4 rounded-full bg-blue-400/50"
										></div>
									</div>
									<div>
										<p
											class="text-xs font-bold text-gray-300"
										>
											{agent.name}
										</p>
										<p
											class="text-[10px] text-blue-300 animate-pulse"
										>
											{agent.action}...
										</p>
									</div>
								</div>
							{/each}
						</div>
					</button>

					<!-- Room: Architecture -->
					<button
						class="group relative rounded-2xl border transition-all duration-300 p-6 flex flex-col justify-between overflow-hidden text-left
						{activeRoom === 'architecture'
							? 'bg-[#1c1c2e] border-purple-400/50 shadow-[0_0_30px_rgba(168,85,247,0.15)] ring-1 ring-purple-400/30'
							: 'bg-[#1c1c2e]/40 border-white/5 hover:bg-[#1c1c2e]/60'}"
						on:click={() => (activeRoom = "architecture")}
					>
						<div
							class="flex justify-between items-start w-full relative z-10"
						>
							<div class="flex items-center gap-2">
								<span
									class="w-2 h-2 rounded-full bg-purple-400 animate-pulse"
								></span>
								<h3
									class="font-bold text-white text-sm uppercase tracking-wide"
								>
									Architecture
								</h3>
							</div>
							<svg
								class="w-5 h-5 text-gray-500 group-hover:text-purple-400 transition-colors"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d={rooms.architecture.icon}
								/>
							</svg>
						</div>

						<!-- Agents -->
						<div class="space-y-3 relative z-10 mt-4">
							{#each rooms.architecture.agents as agent}
								<div class="flex items-center gap-3">
									<div
										class="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/20"
									>
										<div
											class="w-4 h-4 rounded-md border border-purple-400/50"
										></div>
									</div>
									<div>
										<p
											class="text-xs font-bold text-gray-300"
										>
											{agent.name}
										</p>
										<p
											class="text-[10px] text-purple-300 animate-pulse"
										>
											{agent.action}...
										</p>
									</div>
								</div>
							{/each}
						</div>
					</button>

					<!-- Room: Quality (Bottom Left) -->
					<button
						class="group relative rounded-2xl border transition-all duration-300 p-6 flex flex-col justify-between overflow-hidden text-left
						{activeRoom === 'quality'
							? 'bg-[#1c1c2e] border-yellow-400/50 shadow-[0_0_30px_rgba(250,204,21,0.15)] ring-1 ring-yellow-400/30'
							: 'bg-[#1c1c2e]/40 border-white/5 hover:bg-[#1c1c2e]/60'}"
						on:click={() => (activeRoom = "quality")}
					>
						<div
							class="flex justify-between items-start w-full relative z-10"
						>
							<div class="flex items-center gap-2">
								<span
									class="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"
								></span>
								<h3
									class="font-bold text-white text-sm uppercase tracking-wide"
								>
									QA & Security
								</h3>
							</div>
							<svg
								class="w-5 h-5 text-gray-500 group-hover:text-yellow-400 transition-colors"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d={rooms.quality.icon}
								/>
							</svg>
						</div>

						<div class="space-y-3 relative z-10 mt-4">
							{#each rooms.quality.agents as agent}
								<div class="flex items-center gap-3">
									<div
										class="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20"
									>
										<div
											class="w-4 h-4 rounded-full border-2 border-yellow-400/50"
										></div>
									</div>
									<div>
										<p
											class="text-xs font-bold text-gray-300"
										>
											{agent.name}
										</p>
										<p
											class="text-[10px] text-yellow-300 animate-pulse"
										>
											{agent.action}...
										</p>
									</div>
								</div>
							{/each}
						</div>
					</button>

					<!-- Room: Engineering (Bottom Right) -->
					<button
						class="group relative rounded-2xl border transition-all duration-300 p-6 flex flex-col justify-between overflow-hidden text-left
						{activeRoom === 'engineering'
							? 'bg-[#1c1c2e] border-emerald-400/50 shadow-[0_0_30px_rgba(52,211,153,0.15)] ring-1 ring-emerald-400/30'
							: 'bg-[#1c1c2e]/40 border-white/5 hover:bg-[#1c1c2e]/60'}"
						on:click={() => (activeRoom = "engineering")}
					>
						<div
							class="flex justify-between items-start w-full relative z-10"
						>
							<div class="flex items-center gap-2">
								<span
									class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"
								></span>
								<h3
									class="font-bold text-white text-sm uppercase tracking-wide"
								>
									Engineering Bay
								</h3>
							</div>
							<svg
								class="w-5 h-5 text-gray-500 group-hover:text-emerald-400 transition-colors"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d={rooms.engineering.icon}
								/>
							</svg>
						</div>

						<div class="space-y-3 relative z-10 mt-4">
							{#each rooms.engineering.agents as agent}
								<div class="flex items-center gap-3">
									<div
										class="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20"
									>
										<div
											class="w-4 h-4 bg-emerald-400/50 transform rotate-45"
										></div>
									</div>
									<div>
										<p
											class="text-xs font-bold text-gray-300"
										>
											{agent.name}
										</p>
										<p
											class="text-[10px] text-emerald-300 animate-pulse"
										>
											{agent.action}...
										</p>
									</div>
								</div>
							{/each}
						</div>
					</button>
				</div>
			</div>

			<!-- Detail Panel -->
			<div class="lg:col-span-4 flex flex-col h-full">
				{#key activeRoom}
					<div
						in:fade={{ duration: 300 }}
						class="flex-grow rounded-3xl border border-white/10 bg-[#13131f] p-8 relative overflow-hidden"
					>
						<!-- Glow -->
						<div
							class="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 blur-[80px] rounded-full pointer-events-none"
						></div>

						<div class="relative z-10">
							<div
								class="text-xs font-mono text-gray-500 mb-2 uppercase tracking-widest"
							>
								Active Zone
							</div>
							<h3 class="text-3xl font-bold text-white mb-4">
								{rooms[activeRoom].name}
							</h3>
							<p
								class="text-gray-400 leading-relaxed mb-8 border-l-2 border-primary/30 pl-4"
							>
								{rooms[activeRoom].description}
							</p>

							<h4
								class="text-sm font-bold text-white mb-4 uppercase tracking-wider"
							>
								Room Outputs
							</h4>
							<ul class="space-y-3">
								{#each rooms[activeRoom].outputs as output}
									<li
										class="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5"
									>
										<span class="text-neon-cyan">✓</span>
										<span class="text-sm text-gray-300"
											>{output}</span
										>
									</li>
								{/each}
							</ul>
						</div>

						<div class="mt-12 pt-8 border-t border-white/10">
							<div class="flex items-center gap-4">
								<div class="flex -space-x-3">
									{#each [1, 2, 3] as i}
										<div
											class="w-8 h-8 rounded-full bg-gray-700 border-2 border-[#13131f] flex items-center justify-center text-[10px] text-white font-bold"
										>
											A{i}
										</div>
									{/each}
								</div>
								<div class="text-xs text-gray-500">
									<strong class="text-white"
										>{rooms[activeRoom].agents.length} Agents</strong
									> active in this room
								</div>
							</div>
						</div>
					</div>
				{/key}
			</div>
		</div>

		<!-- === SECTION 3: HIGHLIGHTS & CTA === -->
		<div class="mt-32 grid gap-6 md:grid-cols-3">
			{#each highlights as item}
				<div
					class="rounded-2xl border border-white/5 bg-[#13131f] p-8 hover:border-white/10 transition-colors"
				>
					<h4 class="mb-3 text-lg font-bold text-white">
						{item.title}
					</h4>
					<p class="text-sm leading-relaxed text-gray-400">
						{item.detail}
					</p>
				</div>
			{/each}
		</div>

		<div
			class="mt-16 rounded-3xl border border-white/10 bg-gradient-to-r from-[#13131f] via-[#1c1c2e] to-[#13131f] p-10 text-center relative overflow-hidden group"
		>
			<div
				class="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
			></div>
			<h3 class="mb-4 text-3xl font-bold relative z-10">
				Ready to start your delivery cycle?
			</h3>
			<p class="mx-auto mb-8 max-w-2xl text-gray-400 relative z-10">
				Start with your operating model and move to a production release
				without losing control over security, reliability, or speed.
			</p>
			<div
				class="flex flex-col justify-center gap-4 sm:flex-row relative z-10"
			>
				<a
					href="/start"
					class="group relative px-8 py-4 bg-primary text-white text-lg font-bold rounded-xl overflow-hidden transition-all duration-300 hover:bg-primary-dark hover:scale-105 shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)]"
				>
					<div
						class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"
					></div>
					<span class="relative z-10">Let's Start</span>
				</a>
				<a
					href="/register?plan=professional"
					class="px-8 py-4 rounded-xl border border-white/10 bg-white/5 text-lg font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/10 hover:border-white/20 hover:text-white"
				>
					Create Account
				</a>
			</div>
		</div>
	</div>
</section>

<style>
	@keyframes flow-h {
		0% {
			transform: translateX(0);
			opacity: 0;
		}
		50% {
			opacity: 1;
		}
		100% {
			transform: translateX(100px);
			opacity: 0;
		}
	}
	@keyframes flow-h-rev {
		0% {
			transform: translateX(0);
			opacity: 0;
		}
		50% {
			opacity: 1;
		}
		100% {
			transform: translateX(-100px);
			opacity: 0;
		}
	}
	@keyframes flow-v {
		0% {
			transform: translateY(0);
			opacity: 0;
		}
		50% {
			opacity: 1;
		}
		100% {
			transform: translateY(100px);
			opacity: 0;
		}
	}
	@keyframes flow-v-rev {
		0% {
			transform: translateY(0);
			opacity: 0;
		}
		50% {
			opacity: 1;
		}
		100% {
			transform: translateY(-100px);
			opacity: 0;
		}
	}

	.animate-flow-h {
		animation: flow-h 2s infinite linear;
	}
	.animate-flow-h-rev {
		animation: flow-h-rev 2s infinite linear;
	}
	.animate-flow-v {
		animation: flow-v 2s infinite linear;
	}
	.animate-flow-v-rev {
		animation: flow-v-rev 2s infinite linear;
	}
</style>
